import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCurrency } from "../context/CurrencyContext";
import { useTranslation } from "react-i18next";
import { KeyRound, Copy, CheckCircle2, Clock, MapPin, Loader2, Smartphone, CreditCard, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const providerNames = { mtn: "MTN Mobile Money", airtel: "Airtel Money", zamtel: "Zamtel Kwacha" };
const providerColors = {
  mtn: { bg: "#FFCB05", ink: "#000" },
  airtel: { bg: "#E60000", ink: "#fff" },
  zamtel: { bg: "#00A551", ink: "#fff" },
};

export default function BookingConfirm() {
  const { id } = useParams();
  const [search] = useSearchParams();
  const nav = useNavigate();
  const { format } = useCurrency();
  const { t } = useTranslation();
  const [booking, setBooking] = useState(null);
  const [pin, setPin] = useState("");
  const [phone, setPhone] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [numberCopied, setNumberCopied] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/bookings/${id}`);
    setBooking(data);
    if (data.guest_phone) setPhone(data.guest_phone);
    return data;
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Load admin-configured Mobile Money receiving numbers so the guest sees the
  // correct payout destination for the network they chose on the property page.
  useEffect(() => {
    axios.get(`${API}/payment-config`).then((r) => setPaymentConfig(r.data)).catch(() => setPaymentConfig(null));
  }, []);

  // Fetch payout / check-in status so we can show either the "I've checked in"
  // button or the confirmed banner. Silent-fail — this is a bonus panel.
  const loadPayoutStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/${id}/payout-status`);
      setPayoutStatus(data);
    } catch { /* not authorised or 404 — hide the check-in panel */ }
  }, [id]);
  useEffect(() => { loadPayoutStatus(); }, [loadPayoutStatus]);

  const confirmCheckIn = async () => {
    try {
      setCheckingIn(true);
      await axios.post(`${API}/bookings/${id}/checkin`);
      toast.success(t("booking.checkInThanks"));
      await loadPayoutStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm check-in");
    } finally {
      setCheckingIn(false);
    }
  };

  // Poll Stripe status if returning from checkout
  useEffect(() => {
    const sessionId = search.get("session_id");
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || attempts > 20) {
          clearInterval(interval);
          setPolling(false);
          await load();
          if (data.payment_status === "paid") toast.success("Payment confirmed!");
        }
      } catch (err) {
        console.error("Payment status poll failed:", err);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [search, load]);

  const payMobileMoney = async () => {
    if (!phone || pin.length < 4) return toast.error("Enter phone and PIN");
    try {
      setPayLoading(true);
      await axios.post(`${API}/payments/mobile-money`, { booking_id: id, phone, pin });
      toast.success("Payment confirmed!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment failed");
    } finally { setPayLoading(false); }
  };

  const payCard = async () => {
    try {
      setPayLoading(true);
      const { data } = await axios.post(`${API}/payments/stripe/checkout`, {
        booking_id: id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setPayLoading(false);
    }
  };

  const copyKey = () => {
    if (!booking.digital_key) return;
    navigator.clipboard.writeText(booking.digital_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!booking) return <div className="mx-auto max-w-3xl px-6 py-16 text-slate-500">{t("common.loading")}</div>;

  const isPaid = booking.payment_status === "paid";
  const isMobileMoney = ["mtn","airtel","zamtel"].includes(booking.payment_method);
  const amount = booking.currency_selected === "USD" ? `$${booking.total_usd.toFixed(2)}` : format(booking.total_zmw);
  const receivingNumber = paymentConfig?.[`${booking.payment_method}_number`] || "";
  const receivingName = paymentConfig?.[`${booking.payment_method}_name`] || "";
  const brand = isMobileMoney ? providerColors[booking.payment_method] : null;

  const copyReceivingNumber = () => {
    if (!receivingNumber) return;
    try { navigator.clipboard.writeText(receivingNumber); } catch { /* clipboard blocked */ }
    setNumberCopied(true);
    setTimeout(() => setNumberCopied(false), 2000);
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10 lg:px-8">
      <div className="rounded-3xl bg-white p-8 card-shadow">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Booking</div>
            <h1 className="mt-1 font-display text-2xl font-bold text-slate-900 sm:text-3xl">{booking.property_title}</h1>
          </div>
          <div className={`rounded-full px-3 py-1 text-xs font-semibold ${isPaid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-800"}`}>
            {isPaid ? t("booking.paid") : t("booking.pending")}
          </div>
        </div>

        {/* Check-in confirmation panel (guest, host, or admin can confirm).
            Only shows once the booking is paid — before that we're still in
            the payment flow. Once confirmed, the host payout enters the
            admin's release queue. */}
        {isPaid && payoutStatus && (
          <div className="mt-4">
            {payoutStatus.check_in_confirmed_at ? (
              <div
                className="flex items-start gap-3 rounded-2xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-forest"
                data-testid="checkin-confirmed-banner"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">{t("booking.checkInConfirmed")}</div>
                  <div className="mt-0.5 text-xs">
                    {new Date(payoutStatus.check_in_confirmed_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-copper/30 bg-copper/5 px-4 py-3">
                <div className="min-w-0 flex-1 text-sm text-slate-700">
                  <div className="font-semibold text-slate-900">{t("booking.checkInHelp")}</div>
                </div>
                <button
                  onClick={confirmCheckIn}
                  disabled={checkingIn}
                  data-testid="checkin-btn"
                  className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  {checkingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                  {t("booking.checkInNow")}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-4 text-sm sm:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Check-in</div>
            <div className="mt-1 flex items-center gap-1 font-medium text-slate-900"><Clock className="h-4 w-4 text-copper" /> {new Date(booking.check_in).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Duration</div>
            <div className="mt-1 font-medium text-slate-900">{booking.duration_hours}h</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Key type</div>
            <div className="mt-1 flex items-center gap-1 font-medium capitalize text-slate-900"><KeyRound className="h-4 w-4 text-copper" /> {booking.key_type}</div>
          </div>
        </div>

        <div className="my-6 h-px bg-slate-100" />

        {!isPaid && (
          <div className="space-y-4">
            <h3 className="font-display text-lg font-bold text-slate-900">Complete payment · {amount}</h3>

            {polling && (
              <div className="flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <Loader2 className="h-4 w-4 animate-spin" /> Confirming your card payment…
              </div>
            )}

            {isMobileMoney && (
              <div className="rounded-2xl border border-slate-200 p-5" data-testid="mm-payment-panel">
                <div className="mb-3 flex items-center gap-2 font-semibold text-slate-900">
                  <span
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full"
                    style={{ background: brand?.bg, color: brand?.ink }}
                  >
                    <Smartphone className="h-4 w-4" />
                  </span>
                  {providerNames[booking.payment_method]}
                </div>

                {/* Admin-configured receiving number for this network */}
                {receivingNumber ? (
                  <div
                    className="mb-4 rounded-xl border-2 p-4"
                    style={{ borderColor: brand?.bg, background: `${brand?.bg}12` }}
                    data-testid="mm-receiving-panel"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                      Send {amount} to
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-3">
                      <div>
                        <div data-testid="mm-receiving-number" className="font-display text-2xl font-bold text-slate-900 tracking-wide">
                          {receivingNumber}
                        </div>
                        {receivingName && (
                          <div className="mt-0.5 text-xs text-slate-600" data-testid="mm-receiving-name">{receivingName}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={copyReceivingNumber}
                        data-testid="mm-copy-number-btn"
                        className="btn-outline text-xs !py-2 !px-3"
                      >
                        <Copy className="mr-1 inline h-3.5 w-3.5" />
                        {numberCopied ? "Copied" : "Copy"}
                      </button>
                    </div>
                    {paymentConfig?.instructions && (
                      <div className="mt-3 text-xs text-slate-600">{paymentConfig.instructions}</div>
                    )}
                  </div>
                ) : (
                  <div
                    className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900"
                    data-testid="mm-receiving-missing"
                  >
                    A {providerNames[booking.payment_method]} receiving number hasn&apos;t been configured yet. Please contact
                    support at support@easystayzm.com to complete this payment.
                  </div>
                )}

                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">Mobile number</label>
                    <input
                      data-testid="mm-phone-input"
                      type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="+260 977 000 000"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">PIN</label>
                    <input
                      data-testid="mm-pin-input"
                      type="password" value={pin} onChange={(e) => setPin(e.target.value)}
                      placeholder="****"
                      className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-500">Simulated: any PIN 4+ digits works. PINs ending in 00000 are declined.</div>
                <button data-testid="mm-pay-btn" onClick={payMobileMoney} disabled={payLoading} className="btn-primary mt-4 w-full text-sm">
                  {payLoading ? "Processing…" : `Pay ${amount}`}
                </button>
              </div>
            )}

            {booking.payment_method === "card" && (
              <div className="rounded-2xl border border-slate-200 p-5">
                <div className="mb-3 flex items-center gap-2 font-semibold text-slate-900">
                  <CreditCard className="h-5 w-5 text-copper" /> Visa / Mastercard (Stripe secure checkout)
                </div>
                <p className="text-sm text-slate-600">You&apos;ll be redirected to Stripe&apos;s secure page. Amount charged in USD (${booking.total_usd.toFixed(2)}).</p>
                <button data-testid="card-pay-btn" onClick={payCard} disabled={payLoading} className="btn-primary mt-4 w-full text-sm">
                  {payLoading ? "Redirecting…" : `Pay with card`}
                </button>
              </div>
            )}
          </div>
        )}

        {isPaid && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800">
              <CheckCircle2 className="h-5 w-5" /> Payment confirmed · Ref {booking.payment_ref}
            </div>

            {booking.key_type === "digital" ? (
              booking.key_delivered ? (
                <div className="rounded-2xl border-2 border-copper/30 bg-copper/5 p-6">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
                    <KeyRound className="h-4 w-4" /> Your digital key
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div data-testid="digital-key-display" className="font-display text-3xl font-bold text-slate-900 tracking-wider">{booking.digital_key}</div>
                    <button onClick={copyKey} data-testid="copy-key-btn" className="btn-outline text-xs !py-2 !px-4">
                      <Copy className="mr-1 inline h-3.5 w-3.5" /> {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                  <div className="mt-2 text-xs text-slate-600">Present this code at check-in. Keep it private.</div>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 p-5">
                  <div className="flex items-center gap-2 font-semibold text-slate-900">
                    <KeyRound className="h-5 w-5 text-copper" /> Digital key pending
                  </div>
                  <p className="mt-1 text-sm text-slate-600">Your host will send your digital key here shortly before check-in. Watch this page.</p>
                </div>
              )
            ) : (
              <div className="rounded-2xl border border-slate-200 p-5">
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <MapPin className="h-5 w-5 text-copper" /> Collect your physical key
                </div>
                <p className="mt-2 text-sm text-slate-700">Please collect from the address confirmed by your host on the property page.</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 flex gap-3">
          <button onClick={() => nav("/my/bookings")} className="btn-outline text-sm">My bookings</button>
          <button onClick={() => nav("/search")} className="btn-outline text-sm">Book another</button>
        </div>
      </div>
    </div>
  );
}
